import { GRID_CONFIG, sortByAuditUpdatedOn } from "../config";
import axios from "axios";
import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
    FaSearch,
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
    FaCalendarAlt,
    FaTimes,
} from "react-icons/fa";

const SecurityLog = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [selectedLog, setSelectedLog] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    

    const filteredConversations = logs.filter((log) => {
        const searchTerm = search.toLowerCase();

        // Text search filter
        const matchesSearchTerm =
            (log.first_name &&
                log.first_name.toLowerCase().includes(searchTerm)) ||
            (log.last_name &&
                log.last_name.toLowerCase().includes(searchTerm)) ||
            (log.event_type &&
                log.event_type.toLowerCase().includes(searchTerm)) ||
            (log.reason && log.reason.toLowerCase().includes(searchTerm));

        // Date range filter
        let matchesDateRange = true;
        if(startDate || endDate) {
            const logDate = new Date(log.timestamp);

            // Check if log date is after start date (if specified)
            if(startDate) {
                const startOfDay = new Date(startDate);
                startOfDay.setHours(0, 0, 0, 0);
                if(logDate < startOfDay) {
                    matchesDateRange = false;
                }
            }

            // Check if log date is before end date (if specified)
            if(endDate && matchesDateRange) {
                const endOfDay = new Date(endDate);
                endOfDay.setHours(23, 59, 59, 999);
                if(logDate > endOfDay) {
                    matchesDateRange = false;
                }
            }
        }

        return matchesSearchTerm && matchesDateRange;
    });

    const totalPages = Math.ceil(filteredConversations.length / perPage);
    const paginatedData = filteredConversations.slice(
        (currentPage - 1) * perPage,
        currentPage * perPage,
    );
    const fromRecord = (currentPage - 1) * perPage + 1;
    const toRecord = Math.min(
        currentPage * perPage,
        filteredConversations.length,
    );
    const totalRecords = filteredConversations.length;

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--navy)",
                color: "white",
                fontWeight: "bold",
                fontSize: "16px",
                minHeight: "50px",
            },
        },
        headCells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
            },
        },
        cells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
                fontSize: "14px",
            },
        },
        rows: {
            style: {
                cursor: "pointer",
                minHeight: "45px",
                "&:hover": {
                    backgroundColor: "#f1f8ff",
                },
            },
            stripedStyle: {
                backgroundColor: "#F5F7FA",
            },
        },

        pagination: {
            style: {
                borderTop: "none",
            },
        },
    };

    // Columns for security log
    const columns = [
        {
            name: "First Name",
            selector: (row) => row.first_name || "Unknown",
            sortable: true,
            width: "10%",
        },
        {
            name: "Last Name",
            selector: (row) => row.last_name || "Unknown",
            sortable: true,
            width: "10%",
        },
        {
            name: "Type",
            selector: (row) => row.event_type,
            sortable: true,
            width: "15%",
        },
        {
            name: "Result",
            selector: (row) => row.event_result,
            sortable: true,
            width: "9%",
            cell: (row) => (
                <span
                    style={{
                        color:
                            row.event_result?.toLowerCase() === "success"
                                ? "green"
                                : "red",
                        fontWeight: "bold",
                    }}
                >
                    {row.event_result}
                </span>
            ),
        },

        // },

        {
            name: "Reason",
            selector: (row) => row.reason,
            sortable: true,
            width: "17%",
            wrap: true,
            cell: (row) => (
                <span
                    style={{
                        color: row.reason ? "#ff6b35" : "#6c757d",
                        fontStyle: row.reason ? "normal" : "italic",
                    }}
                >
                    {row.reason || "No reason provided"}
                </span>
            ),
        },
        {
            name: "IP Address",
            selector: (row) => row.ip_address || "N/A",
            sortable: true,
            width: "12%",
        },
        {
            name: "User Agent",
            selector: (row) => row.user_agent || "N/A",
            sortable: true,
            width: "12%",
            cell: (row) => (
                <span
                    title={row.user_agent || "N/A"}
                    style={{
                        display: "inline-block",
                        maxWidth: "50px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: "#333",
                    }}
                >
                    {row.user_agent || "N/A"}
                </span>
            ),
        },
        {
            name: "Timestamp",
            selector: (row) => new Date(row.timestamp).toLocaleString(),
            sortable: true,
            width: "15%",
        },
    ];

    // Fetch security logs
    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/security_log`,
                    {
                        withCredentials: true,
                    },
                );
                setLogs(sortByAuditUpdatedOn(response.data));
                setLoading(false);
            } catch(error) {
                console.error("Error fetching security logs:", error);
                setLoading(false);
            }
        };
        fetchLogs();
    }, []);

    // Modal handlers
    const handleRowClick = (row) => {
        setSelectedLog(row);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedLog(null);
    };

    const clearAllFilters = () => {
        setSearch("");
        setStartDate(null);
        setEndDate(null);
        setCurrentPage(1);
    };

    // Function to export logs to CSV
    const exportToCSV = () => {
        try {
            // Headers for CSV
            const headers = [
                "First Name",
                "Last Name",
                "Type",
                "Result",
                "Reason",
                "IP Address",
                "User Agent",
                "Timestamp",
                "Email",
                "User ID",
                "Account Status",
                "Failed Attempts",
                "Last Password Change",
                "Last Activity",
            ].join(",");

            // Format data rows for CSV
            const csvRows = filteredConversations.map((log) => {
                const accountStatus = log.is_locked ? "LOCKED" : "ACTIVE";
                const timestamp = new Date(log.timestamp).toLocaleString();
                const lastPwdChange = log.last_pwd_changed_at
                    ? new Date(log.last_pwd_changed_at).toLocaleString()
                    : "Never";
                const lastActivity = log.last_activity_at
                    ? new Date(log.last_activity_at).toLocaleString()
                    : "Never";

                // Escape fields that might contain commas
                const escapeComma = (field) => {
                    if(
                        field &&
                        typeof field === "string" &&
                        field.includes(",")
                    ) {
                        return `"${field.replace(/"/g, '""')}"`;
                    }
                    return field || "";
                };

                return [
                    escapeComma(log.first_name || "Unknown"),
                    escapeComma(log.last_name || "Unknown"),
                    escapeComma(log.event_type),
                    escapeComma(log.event_result),
                    escapeComma(log.reason),
                    escapeComma(log.ip_address || "N/A"),
                    escapeComma(log.user_agent || "N/A"),
                    escapeComma(timestamp),
                    escapeComma(log.email || "N/A"),
                    escapeComma(log.user_id || "N/A"),
                    accountStatus,
                    log.wrong_pwd_count || "0",
                    escapeComma(lastPwdChange),
                    escapeComma(lastActivity),
                ].join(",");
            });

            // Combine headers and data
            const csvContent = [headers, ...csvRows].join("\n");

            // Create a blob and download link
            const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");

            // Set filename with current date
            const now = new Date();
            const filename = `security_logs_${
                now.toISOString().split("T")[0]
            }.csv`;

            link.setAttribute("href", url);
            link.setAttribute("download", filename);
            link.style.visibility = "hidden";

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch(error) {
            console.error("Error exporting to CSV:", error);
            alert("Failed to export data to CSV. Please try again.");
        }
    };

    const CustomPagination = () => (
        <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
            <div className="flex flex-col">
                <span>
                    Showing{" "}
                    {filteredConversations.length === 0 ? 0 : fromRecord} to{" "}
                    {toRecord} of {totalRecords} records
                </span>
            </div>

            <div className="flex gap-2 text-lg items-center">
                <button
                    disabled={currentPage === 1 || loading}
                    onClick={() => setCurrentPage(1)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleLeft />
                </button>
                <button
                    disabled={currentPage === 1 || loading}
                    onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleLeft />
                </button>
                <span className="text-sm px-2">
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    disabled={currentPage === totalPages || loading}
                    onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleRight />
                </button>
                <button
                    disabled={currentPage === totalPages || loading}
                    onClick={() => setCurrentPage(totalPages)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleRight />
                </button>
            </div>
        </div>
    );

    return (
        <div
            className="aqi-grid-page"
            style={{
                padding: "30px",
                width: "90%",
                margin: "30px auto",
                boxShadow: "0 0 20px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                backgroundColor: "white",
                position: "relative",
            }}
        >
            <h2
                style={{
                    color: "var(--navy)",
                    marginBottom: "20px",
                    fontSize: "large",
                    fontFamily: "large",
                }}
            >
                SECURITY LOGS
            </h2>

            <div className="aqi-grid-toolbar">
                {/* Rows per page */}
                <select
                    style={{
                        padding: "0.375rem 2rem 0.375rem 0.75rem",
                        color: "var(--navy)",
                        backgroundColor: "#F5F7FA",
                        border: "2px solid #C2D7E2",
                        borderRadius: "0.375rem",
                        fontWeight: "500",
                        appearance: "none",
                        backgroundImage:
                            "var(--navy-select-chevron)",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 0.5rem center",
                        backgroundSize: "16px 12px",
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                        outline: "none",
                    }}
                    className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                    value={perPage}
                    onChange={(e) => {
                        setPerPage(Number(e.target.value));
                        setCurrentPage(1);
                    }}
                >
                    {GRID_CONFIG.pageSizeOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                    ))}
                </select>

                {/* Date range picker container */}
                <div className="flex gap-2 items-center flex-wrap">
                    <div className="relative">
                        <div
                            className="aqi-grid-control flex items-center border-2 border-[#C2D7E2] rounded-md bg-[#F5F7FA] px-2 cursor-pointer text-navy hover:border-navy focus-within:border-navy focus-within:ring-1 focus-within:ring-navy focus-within:shadow-sm transition-all"
                        >
                            <FaCalendarAlt className="text-navy mr-2" />
                            <DatePicker
                                selected={startDate}
                                onChange={(date) => {
                                    setStartDate(date);
                                    setCurrentPage(1);
                                }}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                placeholderText="Start Date"
                                dateFormat="MM/dd/yyyy"
                                className="bg-transparent border-0 outline-none text-sm p-1 w-28"
                            />
                            {startDate && (
                                <button
                                    onClick={() => setStartDate(null)}
                                    className="text-gray-400 hover:text-red-500"
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: "0 4px",
                                    }}
                                >
                                    <FaTimes size={12} />
                                </button>
                            )}
                        </div>
                    </div>

                    <span className="text-navy">to</span>

                    <div className="relative">
                        <div
                            className="aqi-grid-control flex items-center border-2 border-[#C2D7E2] rounded-md bg-[#F5F7FA] px-2 cursor-pointer text-navy hover:border-navy focus-within:border-navy focus-within:ring-1 focus-within:ring-navy focus-within:shadow-sm transition-all"
                        >
                            <FaCalendarAlt className="text-navy mr-2" />
                            <DatePicker
                                selected={endDate}
                                onChange={(date) => {
                                    setEndDate(date);
                                    setCurrentPage(1);
                                }}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate}
                                placeholderText="End Date"
                                dateFormat="MM/dd/yyyy"
                                className="bg-transparent border-0 outline-none text-sm p-1 w-28"
                            />
                            {endDate && (
                                <button
                                    onClick={() => setEndDate(null)}
                                    className="text-gray-400 hover:text-red-500"
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: "0 4px",
                                    }}
                                >
                                    <FaTimes size={12} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Search box */}
                <div className="aqi-grid-search">
                    <FaSearch
                        className="absolute left-3 top-2.5"
                        style={{ color: "var(--navy)" }}
                    />
                    <input
                        type="text"
                        placeholder="Search logs..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setCurrentPage(1);
                        }}
                        style={{
                            paddingLeft: "2.5rem",
                            paddingRight: "0.75rem",
                            width: "100%",
                            borderRadius: "0.375rem",
                            border: "2px solid #C2D7E2",
                            backgroundColor: "#F5F7FA",
                            transition: "all 0.2s ease",
                            outline: "none",
                            color: "var(--navy)",
                        }}
                        className="aqi-grid-search-input hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                    />
                </div>

                {/* Clear filters button */}
                {(search || startDate || endDate) && (
                    <button
                        onClick={clearAllFilters}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "0.5rem 1rem",
                            backgroundColor: "#F5F7FA",
                            border: "2px solid #C2D7E2",
                            borderRadius: "0.375rem",
                            color: "var(--navy)",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                        }}
                        className="aqi-grid-button aqi-grid-button--primary"
                    >
                        <FaTimes size={14} />
                        <span>Clear Filters</span>
                    </button>
                )}

                {/* Export to CSV button */}
                <div className="aqi-grid-toolbar-actions">
                    <button
                        onClick={exportToCSV}
                        title="Export CSV"
                        disabled={filteredConversations.length === 0}
                        className={`aqi-grid-button aqi-grid-button--primary ${
                            filteredConversations.length === 0 ? "opacity-70 cursor-not-allowed" : ""
                        }`}
                    >
                        Export
                    </button>
                </div>
            </div>

            <DataTable
                columns={columns}
                data={paginatedData}
                customStyles={customStyles}
                onRowClicked={handleRowClick}
                progressPending={loading}
                pagination={false}
                highlightOnHover
                pointerOnHover
                responsive
                striped
            />

            <CustomPagination />

            {/* Security Log Detail Modal */}
            {showModal && selectedLog && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "white",
                            padding: "30px",
                            borderRadius: "8px",
                            width: "80%",
                            maxWidth: "800px",
                            maxHeight: "90vh",
                            overflow: "auto",
                            boxShadow: "0 0 30px rgba(0,0,0,0.2)",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "20px",
                                borderBottom: "1px solid #eee",
                                paddingBottom: "10px",
                            }}
                        >
                            <h2 style={{ color: "#007bff", margin: 0 }}>
                                Security Event Details
                            </h2>
                            <button
                                onClick={closeModal}
                                style={{
                                    background: "#007bff",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "4px",
                                    padding: "8px 15px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                }}
                            >
                                Close
                            </button>
                        </div>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "20px",
                                marginBottom: "20px",
                            }}
                        >
                            <div>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    First Name:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {selectedLog.first_name || "N/A"}
                                </p>
                            </div>
                            <div>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Last Name:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {selectedLog.last_name || "N/A"}
                                </p>
                            </div>
                        </div>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "20px",
                                marginBottom: "20px",
                            }}
                        >
                            <div>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    User ID:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {selectedLog.user_id || "N/A"}
                                </p>
                                <div>
                                    <h3
                                        style={{
                                            color: "#555",
                                            marginBottom: "5px",
                                        }}
                                    >
                                        Email:
                                    </h3>
                                    <p style={{ margin: 0 }}>
                                        {selectedLog.email || "N/A"}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "20px",
                                marginBottom: "20px",
                            }}
                        >
                            <div>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Event Type:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {selectedLog.event_type}
                                </p>
                            </div>
                            <div>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Result:
                                </h3>
                                <p
                                    style={{
                                        margin: 0,
                                        color:
                                            selectedLog.event_result?.toLowerCase() ===
                                            "success"
                                                ? "green"
                                                : "red",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {selectedLog.event_result?.toLowerCase()}
                                </p>
                            </div>
                        </div>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "20px",
                                marginBottom: "20px",
                            }}
                        >
                            <div>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Account Status:
                                </h3>
                                <p
                                    style={{
                                        margin: 0,
                                        color: selectedLog.is_locked
                                            ? "red"
                                            : "green",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {selectedLog.is_locked
                                        ? "LOCKED"
                                        : "ACTIVE"}
                                </p>
                            </div>
                            <div>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Failed Attempts:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {selectedLog.wrong_pwd_count}
                                </p>
                            </div>
                        </div>
                        {selectedLog.reason && (
                            <div style={{ marginBottom: "20px" }}>
                                <h3 style={{ marginBottom: "5px" }}>Reason:</h3>
                                <div
                                    style={{
                                        // backgroundColor: selectedLog.reason.toLowerCase() === 'login_success' ? '#d4edda' : '#f8d7da',
                                        padding: "10px",
                                        borderRadius: "4px",
                                        borderLeft: "4px solid",
                                        borderColor:
                                            selectedLog.reason.toLowerCase() ===
                                            "login_success"
                                                ? "#28a745"
                                                : "#dc3545",
                                    }}
                                >
                                    <p style={{ margin: 0 }}>
                                        {selectedLog.reason}
                                    </p>
                                </div>
                            </div>
                        )}
                        <div style={{ marginBottom: "20px" }}>
                            <h3 style={{ color: "#555", marginBottom: "5px" }}>
                                Password History:
                            </h3>
                            <div
                                style={{
                                    backgroundColor: "#F5F7FA",
                                    padding: "15px",
                                    borderRadius: "4px",
                                    maxHeight: "150px",
                                    overflowY: "auto",
                                }}
                            >
                                {selectedLog.password_history ? (
                                    <pre
                                        style={{
                                            margin: 0,
                                            whiteSpace: "pre-wrap",
                                            fontFamily: "monospace",
                                            fontSize: "13px",
                                        }}
                                    >
                                        {JSON.stringify(
                                            JSON.parse(
                                                selectedLog.password_history,
                                            ),
                                            null,
                                            2,
                                        )}
                                    </pre>
                                ) : (
                                    <p style={{ margin: 0, color: "#6c757d" }}>
                                        No password history available
                                    </p>
                                )}
                            </div>
                        </div>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr",
                                gap: "20px",
                            }}
                        >
                            <div>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Last Password Change:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {selectedLog.last_pwd_changed_at
                                        ? new Date(
                                              selectedLog.last_pwd_changed_at,
                                          ).toLocaleString()
                                        : "Never"}
                                </p>
                            </div>
                            <div>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Last Activity:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {selectedLog.last_activity_at
                                        ? new Date(
                                              selectedLog.last_activity_at,
                                          ).toLocaleString()
                                        : "Never"}
                                </p>
                            </div>
                        </div>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "20px",
                                marginTop: "20px",
                            }}
                        >
                            <div>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    IP Address:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {selectedLog.ip_address || "N/A"}
                                </p>
                            </div>
                            <div>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    User Agent:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {selectedLog.user_agent || "N/A"}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
export default SecurityLog;
